import { graphql } from 'react-relay';
import type { FC } from 'react';
import React, { useContext, useRef, useCallback } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import type { JSONDocNode } from '@atlaskit/editor-json-transformer/types';
import type { DocNode } from '@atlaskit/adf-schema/doc';
import { ChromelessEditor } from '@atlaskit/editor-core/appearance-editor-chromeless';
import { CommentEditor as AkCommentEditor } from '@atlaskit/editor-core/appearance-editor-comment';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Box from '@atlaskit/primitives/box';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { xcss } from '@atlaskit/primitives/xcss';
import { token } from '@atlaskit/tokens';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import type { MutationUpdaterInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import {
	EDIT_PAGE_COMMENT_LOAD_EXPERIENCE,
	EDIT_PAGE_COMMENT_PUBLISH_EXPERIENCE,
	EDIT_INLINE_COMMENT_LOAD_EXPERIENCE,
	EDIT_INLINE_COMMENT_PUBLISH_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { startEditorSubExperience } from '@confluence/experience-tracker/entry-points/startExperienceForRoute.ts';
import { ExperienceTimeout } from '@confluence/experience-tracker/entry-points/ExperienceTimeout.ts';
import { isOptimisticSaveEnabledForTypeAndMode } from '@confluence/comment/entry-points/draftUtils.ts';
import { CommentEditor } from '@confluence/comment/entry-points/LazyCommentEditor.ts';
import type { CommentType } from '@confluence/comments-data-manager/entry-points/commentDataManagerUtils.ts';
import { CommentType as CommentTypeEnum } from '@confluence/comments-data-manager/entry-points/commentDataManagerUtils.ts';
import {
	useUpdateCommentInCache,
	updateCommentBodyInCacheCallback,
} from '@confluence/comments-data-manager/entry-points/useUpdateCommentInCache.ts';
import { useCommentsContentActions } from '@confluence/comment-context/entry-points/useCommentsContent.ts';
import {
	useAnnotationsDispatch,
	useAnnotations,
} from '@confluence/annotation-provider-store/entry-points/AnnotationsContext.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import type {
	CommentInlineResolveProperties,
	FooterResolvedProperties,
} from '@confluence/comments-data-manager/entry-points/useAllCommentsData.ts';
import { getLogger } from '@confluence/logger';
import { END } from '@confluence/navdex/entry-points/NavdexPointType.ts';
import { fg } from '@confluence/feature-gating';
import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import type { WithFlagsProps } from '@confluence/flags/entry-points/withFlags.ts';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode.ts';
import { PageMode } from '@confluence/page-utils/entry-points/enums.ts';
import { AnalyticsSource } from '@confluence/comments-util/entry-points/analytics.ts';
import { buildMutationContextForNoNetworkError } from '@confluence/comments-util/entry-points/errorUtils.ts';
import { useCommentService } from '@confluence/content-types-bridge-comments';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';

import { ContentRepresentation } from './__types__/EditCommentUpdateCommentMutation.ts';
import type {
	EditCommentUpdateCommentMutation as EditCommentUpdateCommentMutation$data,
	EditCommentUpdateCommentMutationVariables as EditCommentUpdateCommentMutation$variables,
} from './__types__/EditCommentUpdateCommentMutation.ts';
import { WithCurvedBranchingStyle } from './WithCurvedBranchingStyle.tsx';
import type { InlineCommentsMode } from './inlineCommentsTypes.ts';
import {
	parseError,
	getTranslatedError,
	isUnexpectedError,
	isAlreadyDeletedError,
} from './inlineCommentsErrorUtils.ts';
import { CommentAuthor } from './CommentAuthor.tsx';
import { getEditCommentOptimisticResponse } from './hooks/optimisticResponseHelpers.ts';

const EditCommentUpdateCommentMutation = graphql`
	mutation EditCommentUpdateCommentMutation($input: UpdateCommentInput!) {
		updateComment(input: $input) {
			id
			body(representation: ATLAS_DOC_FORMAT) {
				value
			}
			version {
				number
				when
				by {
					displayName
				}
			}
		}
	}
`;
const i18n = defineMessages({
	cannotEditTitle: {
		id: 'inline-comments-common.could.not.edit.title',
		defaultMessage: 'Unable to edit comment',
		description:
			'The title of the flag shown to a user that says their comment could not be edited',
	},
});

const logger = getLogger('inline-comments-common:edit-comment');

type EditCommentProps = {
	pageId: string;
	pageType: string;
	annotationId?: string;
	commentId: string;
	spaceId: string;
	content: string;
	displayCommentInViewMode: () => void;
	isReply: boolean;
	isEdited?: boolean;
	avatarUrl: string;
	date: string;
	dateUrl?: string;
	createdAtNonLocalized?: string;
	versionByDisplayName?: string | null;
	versionDate: string;
	resolveProperties?: CommentInlineResolveProperties | FooterResolvedProperties;
	isDangling: boolean;
	displayName: string;
	mode: InlineCommentsMode;
	hasMediaUploadPermissions: boolean;
	isCommentsPanel?: boolean; // todo, we should make an enum for this
	isFooterComment?: boolean; // todo, we should make an enum for this
	adjustForFooterReplies?: boolean;
	isLastNestedFooterReply?: boolean;
	onCancelEdit?: () => void;
	/**
	 * Return a list of inline node types, which are wrapped by the annotation,
	 * for annotation with given ID.
	 *
	 * The `undefined` will be returned if `editor_inline_comments_on_inline_nodes` is off.
	 *
	 * @todo: Do not forget to remove `undefined` when the
	 *        `editor_inline_comments_on_inline_nodes` is removed.
	 */
	getInlineNodeTypes?: (annotationId: string) => string[] | undefined;
	commentType: CommentType;
	isStandaloneComment?: boolean;
	isMediaViewerComment?: boolean;
	isOffline?: boolean;
	isCurrentUserAnonymous?: boolean;
	isLastUnnestableReply?: boolean;
	hasParentMaxHeight?: boolean;
	numReplies?: number;
};

const editorContainerStyle = xcss({
	paddingTop: 'space.150',
	paddingBottom: 'space.0',
	paddingInline: 'space.150',
});

const panelEditorInlinePaddingStyle = xcss({
	paddingInlineStart: 'space.200',
});

const editorReplyRelativeLeft = xcss({
	position: 'relative',
	// Design requires -1px margin to account for left border which is outside the token system
	left: '-1px',
});

const editorContainerForFooterWrapperStyle = xcss({
	padding: 'unset',
	paddingTop: 'space.100',
});

const footerReplyEditorPaddingStyle = xcss({
	paddingLeft: 'space.050',
});

const editorContainerForFooterExtraLeftPaddingWithBorderStyle = xcss({
	paddingLeft: 'space.300',
	marginLeft: 'space.150',
	marginTop: 'space.050', // If we're doing the border here, we need margin instead of padding so the border is flush with the editor
	borderLeft: `1.5px solid ${token('color.background.accent.gray.subtlest.hovered')}`,
});

const editorContainerForFooterExtraLeftPaddingWithBorderStyleUpdated = xcss({
	paddingLeft: 'space.300',
	marginLeft: 'space.150',
	marginTop: 'space.050', // If we're doing the border here, we need margin instead of padding so the border is flush with the editor
	borderLeft: `${token('border.width')} solid ${token('color.background.accent.gray.subtlest.hovered')}`,
});

const editorContainerForStandaloneWithRepliesStyle = xcss({
	paddingLeft: 'space.300',
	marginLeft: 'space.150',
	borderLeft: `${token('border.width')} solid ${token('color.background.accent.gray.subtlest.hovered')}`,
});

const editorBottomPaddingStyle = xcss({
	paddingBottom: 'space.100',
});

const panelEditorParentContainerStyle = xcss({
	marginLeft: 'space.0',
	marginBottom: 'space.0',
	paddingBlock: 'space.0',
	paddingRight: 'space.0',
	paddingTop: 'space.0',
});

const panelEditorReplyContainerStyle = xcss({
	margin: '0',
	padding: '0',
	paddingTop: 'space.100',
});

const panelReplyEditorPaddingStyle = xcss({
	paddingLeft: 'space.050',
});

const standaloneEditorReplyContainerStyle = xcss({
	margin: '0',
	padding: '0',
	paddingLeft: 'space.050',
	paddingTop: 'space.100',
});

const editorParentWrapperStyle = xcss({
	paddingLeft: 'space.300',
	marginLeft: 'space.negative.100',
	borderLeft: `1.5px solid ${token('color.background.accent.gray.subtlest.hovered')}`,
	paddingBottom: 'space.100',
});

const editorParentWrapperStyleUpdated = xcss({
	paddingLeft: 'space.300',
	marginLeft: 'space.negative.100',
	borderLeft: `${token('border.width')} solid ${token('color.background.accent.gray.subtlest.hovered')}`,
	paddingBottom: 'space.100',
});

const editorParentWithoutRepliesStyle = xcss({
	marginLeft: 'space.negative.050',
	borderLeft: `none`,
});

const editorParentWithRepliesStyle = xcss({
	marginLeft: 'space.negative.050',
	borderLeft: `${token('border.width')} solid ${token('color.background.accent.gray.subtlest.hovered')}`,
});

const standaloneEditorReplyWrapperStyle = xcss({
	paddingLeft: 'space.300',
	paddingBottom: 'space.200',
	marginLeft: 'space.negative.300',
	marginBottom: 'space.negative.150',
});

const EditCommentComponent: FC<EditCommentProps & WithFlagsProps> = ({
	pageId,
	pageType,
	annotationId,
	commentId,
	spaceId,
	content,
	displayCommentInViewMode,
	isReply,
	isEdited,
	avatarUrl,
	date,
	createdAtNonLocalized,
	versionByDisplayName,
	versionDate,
	resolveProperties,
	isDangling,
	displayName,
	mode,
	hasMediaUploadPermissions,
	isCommentsPanel,
	isFooterComment = false,
	adjustForFooterReplies = false,
	isLastNestedFooterReply = false,
	onCancelEdit,
	getInlineNodeTypes,
	commentType,
	flags,
	isStandaloneComment,
	isMediaViewerComment,
	isOffline,
	isCurrentUserAnonymous,
	isLastUnnestableReply = false,
	hasParentMaxHeight = false,
	numReplies = 0,
}) => {
	const { userId, displayName: sessionDisplayName } = useSessionData();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const experienceTracker = useContext(ExperienceTrackerContext);
	const { onChange, resetContentChanged } = useCommentsContentActions();
	const { commentService } = useCommentService();
	const { setStoredCommentData } = useAnnotationsDispatch();
	const { storedCommentData } = useAnnotations();
	const commentContainerElementRef = useRef<HTMLDivElement | null>(null);
	const pageMode = useGetPageMode();
	const [updateCommentFn] = useMutationInterop<
		EditCommentUpdateCommentMutation$data,
		EditCommentUpdateCommentMutation$variables
	>(EditCommentUpdateCommentMutation, buildMutationContextForNoNetworkError());

	// Get the update function for the comments data manager
	const { updateCommentInCache } = useUpdateCommentInCache<EditCommentUpdateCommentMutation$data>();

	const getLoadExperienceName = (type: CommentTypeEnum) =>
		type === CommentTypeEnum.GENERAL
			? EDIT_PAGE_COMMENT_LOAD_EXPERIENCE
			: EDIT_INLINE_COMMENT_LOAD_EXPERIENCE;
	const getPublishExperienceName = (type: CommentTypeEnum) =>
		type === CommentTypeEnum.GENERAL
			? EDIT_PAGE_COMMENT_PUBLISH_EXPERIENCE
			: EDIT_INLINE_COMMENT_PUBLISH_EXPERIENCE;

	const handleOnEditorReady = () => {
		experienceTracker.succeed({
			name: getLoadExperienceName(commentType),
		});
	};

	const handleOnCancel = () => {
		displayCommentInViewMode();
		resetContentChanged();
		onCancelEdit && onCancelEdit();
	};

	const handleEditMutationFailure = useCallback(
		(editMutationError: Error) => {
			experienceTracker.stopOnError({
				name: getPublishExperienceName(commentType),
				error: editMutationError,
			});
		},
		[commentType, experienceTracker],
	);

	const getTypeForCommentEditor = useCallback(() => {
		if (
			commentType === CommentTypeEnum.GENERAL &&
			// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
			fg('confluence_separate_save_comment_ufo_interaction')
		) {
			return 'page';
		} else {
			return mode === 'view' ? 'inline' : 'edit-inline';
		}
	}, [commentType, mode]);

	const editCommentUpdateFn: MutationUpdaterInterop<EditCommentUpdateCommentMutation$data> =
		useCallback(
			(_dataProxy, mutationResult) =>
				updateCommentInCache({
					mutationResult,
					commentId,
					isReply,
					updateCallback: updateCommentBodyInCacheCallback,
					onError: handleEditMutationFailure,
				}),
			[commentId, handleEditMutationFailure, updateCommentInCache, isReply],
		);

	const handleUpdateComment = (adf: JSONDocNode, onSuccess: () => void) => {
		startEditorSubExperience({
			name: getPublishExperienceName(commentType),
			timeout: ExperienceTimeout.EDITOR_PUBLISH,
			attributes: {
				mode: pageMode,
			},
		});

		const adfString = JSON.stringify(adf);
		const variables = {
			input: {
				commentId,
				commentBody: {
					value: adfString,
					representationFormat: ContentRepresentation.ATLAS_DOC_FORMAT,
				},
			},
		};

		const isOptimisticEditEnabled = isOptimisticSaveEnabledForTypeAndMode(
			getTypeForCommentEditor(),
			'edit',
		);
		// When optimistic save is active, immediately switch to view mode so the user
		// sees their edit reflected without waiting for the network. On error we revert.
		if (isOptimisticEditEnabled) {
			onSuccess();
			resetContentChanged();
			displayCommentInViewMode();
		}

		return updateCommentFn({
			variables,
			optimisticResponse: isOptimisticEditEnabled
				? getEditCommentOptimisticResponse({
						commentId,
						adfString,
						sessionDisplayName,
					})
				: undefined,
			update: editCommentUpdateFn,
		})
			.then(() => {
				experienceTracker.succeed({
					name: getPublishExperienceName(commentType),
					attributes: {
						mode: pageMode,
						type: isReply ? 'reply' : 'topLevel',
					},
				});

				createAnalyticsEvent({
					type: 'sendTrackEvent',
					data: {
						action: 'updated',
						actionSubject: 'comment',
						actionSubjectId: commentId,
						objectType: pageType,
						objectId: pageId,
						source: pageMode === PageMode.VIEW ? 'viewPageScreen' : 'editPageScreen',
						attributes: {
							commentType,
							pageType,
							source: isMediaViewerComment
								? AnalyticsSource.MEDIA_VIEWER_SIDEBAR
								: isCommentsPanel
									? AnalyticsSource.COMMENTS_PANEL
									: isFooterComment
										? AnalyticsSource.PAGE_FOOTER
										: AnalyticsSource.STANDALONE,
							pageMode,
							isReply,
							navdexPointType: END,
							isLivePage: pageMode === PageMode.LIVE,
							isDangling,
							inlineNodeTypes:
								getInlineNodeTypes && annotationId ? getInlineNodeTypes(annotationId) : undefined,
						},
					},
				}).fire();

				if (annotationId) {
					// Update content types comment service if we are editing a comment
					commentService.onUpdateComment?.(annotationId, 'edit');
				}

				// Update stored comment data with the new body value
				if (setStoredCommentData && storedCommentData) {
					if (isReply) {
						// For replies, update the specific reply within the stored comment data
						const replies = storedCommentData.replies;
						if (replies && replies.length > 0) {
							const replyIndex = replies.findIndex((reply: any) => reply.id === commentId);
							if (replyIndex !== -1) {
								// Create new replies array with updated reply at specific index
								const updatedReplies = [...replies];
								const reply = updatedReplies[replyIndex];
								if (reply && reply.version) {
									updatedReplies[replyIndex] = {
										...reply,
										body: {
											value: adfString,
										},
										version: {
											...reply.version,
											by: {
												...reply.version.by,
												displayName: sessionDisplayName || reply.version.by?.displayName || null,
											},
											number: (reply.version.number ?? 0) + 1,
											when: new Date().toISOString(),
										},
									};
								}

								setStoredCommentData({
									...storedCommentData,
									replies: updatedReplies,
								});
							}
						}
					} else {
						// For parent comments, update the main comment data
						if (storedCommentData.version) {
							const updatedCommentData = {
								...storedCommentData,
								body: {
									value: adfString,
								},
								version: {
									...storedCommentData.version,
									by: {
										...storedCommentData.version.by,
										displayName:
											sessionDisplayName || storedCommentData.version.by?.displayName || null,
									},
									number: (storedCommentData.version.number ?? 0) + 1,
									when: new Date().toISOString(),
								},
							};
							setStoredCommentData(updatedCommentData);
						}
					}
				}

				// reset the editor — skip if optimistic save already handled this immediately
				if (!isOptimisticEditEnabled) {
					onSuccess();
					resetContentChanged();
					displayCommentInViewMode();
				}
			})
			.catch((error) => {
				const { message } = parseError(error);
				const translatedError = getTranslatedError(message, commentId);

				if (isAlreadyDeletedError(translatedError)) {
					void flags.showWarningFlag({
						title: <FormattedMessage {...i18n.cannotEditTitle} />,
						description: <FormattedMessage {...translatedError} />,
					});

					experienceTracker.abort({
						name: EDIT_INLINE_COMMENT_PUBLISH_EXPERIENCE,
						reason: 'Comment already deleted',
					});
				}

				if (isUnexpectedError(translatedError)) {
					handleEditMutationFailure(error);
				} else {
					// These errors will not be reported to splunk/signalfx through experience tracking as they are expected
					markErrorAsHandled(error);
				}

				return Promise.reject({
					error: translatedError,
				});
			});
	};

	let parsedContent: DocNode | undefined = undefined;
	try {
		parsedContent = JSON.parse(content);
	} catch (e) {
		logger.error`Unable to parse content ADF, ${e}`;
	}

	const showBranchingStyle = (isStandaloneComment || isCommentsPanel) && isReply;

	// Create a wrapper for the editor component. If we have a branching style, the wrapper needs to use withBranchingStyle, otherwise just return as normal
	const BranchingStyleWrapper = useCallback(
		({ children }: { children: React.ReactNode }) => {
			const customHeight = '24px';
			const customLeft = isLastNestedFooterReply || isLastUnnestableReply ? '-16px' : '-17px';

			return showBranchingStyle ? (
				<WithCurvedBranchingStyle
					showDefaultStyles
					customStyles={{
						// If we don't have children, line up with the author
						// If we do have children, line up with the bottom of the editor toolbar
						height: customHeight,
						left: customLeft,
					}}
				>
					{children}
				</WithCurvedBranchingStyle>
			) : (
				<>{children}</>
			);
		},
		[isLastNestedFooterReply, isLastUnnestableReply, showBranchingStyle],
	);

	return (
		<BranchingStyleWrapper>
			<Box
				xcss={[
					editorContainerStyle,
					isCommentsPanel && panelEditorInlinePaddingStyle,
					isReply &&
						!isFooterComment &&
						!(isLastNestedFooterReply || isLastUnnestableReply) &&
						editorReplyRelativeLeft,
					isFooterComment && editorContainerForFooterWrapperStyle,
					isFooterComment && isReply && footerReplyEditorPaddingStyle,
					isCommentsPanel && !isReply && panelEditorParentContainerStyle,
					(isCommentsPanel || isStandaloneComment) && isReply && panelEditorReplyContainerStyle,
					isCommentsPanel && isReply && panelReplyEditorPaddingStyle,
					isStandaloneComment && isReply && standaloneEditorReplyContainerStyle,
				]}
				data-cy="editor-edit-container"
				ref={commentContainerElementRef}
			>
				<CommentAuthor
					commentMode="edit"
					userId={userId}
					date={date}
					createdAtNonLocalized={createdAtNonLocalized}
					versionByDisplayName={versionByDisplayName}
					versionDate={versionDate}
					displayName={displayName}
					avatarUrl={avatarUrl}
					resolveProperties={
						resolveProperties as CommentInlineResolveProperties | FooterResolvedProperties // NOSONAR - Sonar won't allow this cast - typescript requires it
					}
					size="small"
					commentId={isCurrentUserAnonymous ? commentId : null}
					isCommentsPanel={isCommentsPanel}
					isGeneralComments={commentType === CommentTypeEnum.GENERAL}
					isReply={isReply}
					isEdited={isEdited}
					isFooterComment={isFooterComment}
					isStandaloneComment={isStandaloneComment}
				/>
				<Box
					xcss={[
						isFooterComment && editorBottomPaddingStyle,
						isFooterComment &&
							adjustForFooterReplies &&
							editorContainerForFooterExtraLeftPaddingWithBorderStyle,
						isFooterComment &&
							adjustForFooterReplies &&
							// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
							fg('confluence_comment_border_migration') &&
							editorContainerForFooterExtraLeftPaddingWithBorderStyleUpdated,
						isStandaloneComment &&
							!isReply &&
							numReplies > 0 &&
							editorContainerForStandaloneWithRepliesStyle,
						isStandaloneComment && isReply && standaloneEditorReplyWrapperStyle,
						isCommentsPanel && !isReply && editorParentWrapperStyle,
						// eslint-disable-next-line confluence-feature-gating/no-preconditioning
						isCommentsPanel &&
							!isReply &&
							// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
							fg('confluence_comment_border_migration') &&
							editorParentWrapperStyleUpdated,
						isCommentsPanel && !isReply && numReplies === 0 && editorParentWithoutRepliesStyle,
						isCommentsPanel && !isReply && numReplies > 0 && editorParentWithRepliesStyle,
					]}
				>
					<CommentEditor
						pageId={pageId}
						pageType={pageType}
						spaceId={spaceId}
						appearance={isFooterComment ? 'comment' : 'chromeless'}
						EditorComponent={isFooterComment ? AkCommentEditor : ChromelessEditor}
						commentId={commentId}
						commentMode="edit"
						commentType={getTypeForCommentEditor()}
						onSaveComment={handleUpdateComment}
						content={parsedContent}
						onCancelComment={handleOnCancel}
						onContentChange={onChange}
						showCancelButton
						useNewWarningModal
						onEditorReady={handleOnEditorReady}
						hideWatchCheckbox
						pageMode={mode}
						hasMediaUploadPermissions={hasMediaUploadPermissions}
						commentContainerElementRef={commentContainerElementRef}
						isStandaloneComment={isStandaloneComment}
						isFooterComment={isFooterComment}
						isCommentsPanel={isCommentsPanel}
						isMediaViewerComment={isMediaViewerComment}
						isOffline={isOffline}
						hasParentMaxHeight={hasParentMaxHeight}
					/>
				</Box>
			</Box>
		</BranchingStyleWrapper>
	);
};

export const EditComment: React.ComponentType<Omit<EditCommentProps, 'flags'>> =
	withFlags(EditCommentComponent);
